import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Sun, MessageSquare, Menu, X, User, LogOut, Book, Sparkles, Search, Brain, Calendar, HelpingHand, Phone, LogIn } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { NavLink } from './navbar/NavLink';
import { MobileMenu } from './navbar/MobileMenu';
import { AuthModal } from './auth/AuthModal';

export default function Navbar() {
  const { isDark, toggle } = useTheme();
  const { user, signOut, showAuthModal, setShowAuthModal } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    {
      to: '/verses',
      icon: Search,
      text: 'Verse Finder',
      requiresAuth: true
    },
    {
      to: '/daily-motivation',
      icon: Sparkles,
      text: 'Daily Motivation',
      requiresAuth: false
    },
    {
      to: '/monthly-readings',
      icon: Calendar,
      text: 'Monthly Readings',
      requiresAuth: false
    },
    {
      to: '/trivia',
      icon: Brain,
      text: 'Bible Trivia',
      requiresAuth: true
    },
    {
      to: '/prayer-room',
      icon: HelpingHand,
      text: 'Prayer Room',
      requiresAuth: false
    },
    {
      to: '/pray-4-me',
      icon: Phone,
      text: 'Pray 4 Me',
      requiresAuth: false
    },
    {
      to: '/bible',
      icon: Book,
      text: 'Bible',
      requiresAuth: false
    },
    {
      to: '/contact',
      icon: MessageSquare,
      text: 'Contact',
      requiresAuth: false
    }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass-nav shadow-xl'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-2 relative z-10">
            <motion.img
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              src="/logo copy copy.png"
              alt="Verse4All"
              className="h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                icon={item.icon}
                text={item.text}
              />
            ))}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggle}
              className="p-2.5 text-gray-700 hover:text-cyan-600 dark:text-gray-300 dark:hover:text-cyan-400 rounded-xl hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-all duration-200"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </motion.button>

            {user ? (
              <>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/profile"
                    className="p-2.5 text-gray-700 hover:text-cyan-600 dark:text-gray-300 dark:hover:text-cyan-400 rounded-xl hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-all duration-200"
                    aria-label="Profile"
                  >
                    <User className="w-5 h-5" />
                  </Link>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSignOut}
                  className="p-2.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-xl hover:bg-red-50/50 dark:hover:bg-red-900/30 transition-all duration-200"
                  aria-label="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </motion.button>
              </>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAuthModal(true)}
                className="p-2.5 text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 rounded-xl hover:bg-cyan-50/50 dark:hover:bg-cyan-900/30 transition-all duration-200"
                aria-label="Sign in"
              >
                <LogIn className="w-5 h-5" />
              </motion.button>
            )}
          </div>

          {/* Mobile Menu Button - Hidden on small screens, shown on md-lg */}
          <div className="hidden md:flex lg:hidden items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggle}
              className="p-2.5 text-gray-700 hover:text-cyan-600 dark:text-gray-300 dark:hover:text-cyan-400 rounded-xl hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-all duration-200"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2.5 text-gray-700 hover:text-cyan-600 dark:text-gray-300 dark:hover:text-cyan-400 rounded-xl hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-all duration-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <MobileMenu
            navItems={navItems}
            user={user}
            isDark={isDark}
            onToggleTheme={toggle}
            onSignOut={handleSignOut}
            onClose={() => setIsMenuOpen(false)}
            onShowAuth={() => setShowAuthModal(true)}
          />
        )}
      </div>

      {/* Auth Modal */}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </motion.nav>
  );
}